import { PropsWithChildren, useId, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import CrossIcon from '@atlaskit/icon/core/cross';
import ModalDialog from '@atlaskit/modal-dialog';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { Picture } from '@/components/wpl/image/src/image';
import {
	Image,
	ImageProps,
	type WclImageAspectRatio,
} from '@/libraries/design/components/image/src/image';
import { wacToken } from '@/libraries/design/foundations';
import {
	WCL_GRID_MAX_WIDTH,
	WCL_GRID_PADDING_LG,
	WCL_GRID_PADDING_SM,
} from '@/libraries/design/foundations/grid/src/base';
import {
	type IconProps,
	IconSize,
	iconSizesPaddingExcluded,
	iconSizesPaddingIncluded,
} from '@/libraries/design/foundations/icon';
import {
	SPOT_ILLUSTRATION_MAX_HEIGHT,
	SPOT_ILLUSTRATION_WIDTH_ON_HORIZONTAL_CARD,
	type SpotIllustrationProps,
} from '@/libraries/design/foundations/spot-illustration';
import { RichTextComponent } from '@/libraries/design/utils';
import { useDarkTheme } from '@/libraries/design/utils/dark-theme-provider';

import { EmbeddedVideo, PlayIcon } from '../../embedded-video/src/embedded-video';
import { Link, LinkProps } from '../../link/src/link';

export const cardLogoLockups = {
	atlassian: 'https://dam-cdn.atl.orangelogic.com/AssetLink/25o68v5s8lr856c06p606q47s76rs10f.svg',
	guard: 'https://dam-cdn.atl.orangelogic.com/AssetLink/8j5d41rmb7q4himg4r42k3e84hvoo07i.svg',
};

export interface CardProps extends PropsWithChildren {
	eyebrow?: string;
	logo?: keyof typeof cardLogoLockups;
	title?: string | React.ReactElement<typeof FormattedMessage>;
	titleAs?: 'h2' | 'h3' | 'h4' | 'h5' | 'p';
	body?: string | React.ReactElement<typeof RichTextComponent>;
	callToAction?: LinkProps;
	image?: ImageProps;
	imageAspectRatio?: WclImageAspectRatio;
	spotIllustration?: SpotIllustrationProps;
	videoSrc?: string;
	orientation?: 'vertical' | 'horizontal';
	icon?: IconProps;
	align?: 'start' | 'center';
	containerType?: keyof JSX.IntrinsicElements;
	columns?: 10 | 12;
	background?: 'light' | 'dark';
}

export const Card = ({
	align = 'start',
	// we currently do not support a horizontal orientation card with an image
	// the outstanding issue is that the the image has a requirement of an aspect ratio of 16:9
	// and the card text is unbounded (so prioritizing the image would make the text potentially
	// overflow), and prioritizing the text would make the image potentially have undesired
	// white space at the end
	orientation = 'vertical',
	columns,
	background = 'light',
	eyebrow,
	logo,
	title,
	body,
	image,
	imageAspectRatio = '16 / 9',
	icon: {
		linkedIcon: {
			url: iconUrl,
			size: iconSize = 'medium' as IconSize,
			paddingIncluded = false,
		} = {},
		iconComponent,
	} = {},
	callToAction,
	spotIllustration,
	videoSrc,
	...props
}: // eslint-disable-next-line complexity
CardProps) => {
	// hedge against the value being passed in as a string (the options in uniform can only be string values)
	// which would subsequently fail a string vs number check ie 10 === "10" is false
	const numColumns = Number(columns);
	const { titleAs: TitleComponent = 'h3', containerType: Container = 'div' } = props;
	const { isDarkTheme } = useDarkTheme();
	const isDark = background === 'dark' || isDarkTheme;
	const isLinkableCard = !!callToAction;

	// React's useId() returns strings like ":R0:" which contain colons — colons are valid
	// in HTML id attributes but invalid in CSS id selectors without escaping. Compiled CSS
	// generates :has() selectors that reference elements by their id, and jsdom's nwsapi
	// cannot parse unescaped colons in generated selectors. Sanitize by replacing colons
	// with hyphens to produce CSS-safe ids like "-R0-".
	const uniqueId = useId().replace(/:/g, '-');
	const titleId = `card-title-${uniqueId}`;
	const callToActionId = `card-cta-${uniqueId}`;
	const [isVideoOverlayOpen, setIsVideoOverlayOpen] = useState(false);

	const bodyElement: React.ReactNode =
		typeof body === 'string' ? <p css={resetMargin}>{body}</p> : body;

	const openVideoOverlay = (event: React.MouseEvent) => {
		event.preventDefault();
		setIsVideoOverlayOpen(true);
	};

	const videoOverlay = videoSrc
		? isVideoOverlayOpen && (
				<ModalDialog
					onClose={() => setIsVideoOverlayOpen(false)}
					label={typeof title === 'string' ? title : callToAction?.text}
					width="x-large"
				>
					<div css={videoOverlayContent}>
						<EmbeddedVideo
							embedUrl={videoSrc}
							title={typeof title === 'string' ? title : callToAction?.text}
							autoPlay
						/>
						<button
							type="button"
							onClick={() => setIsVideoOverlayOpen(false)}
							css={videoOverlayCloseButton}
						>
							<CrossIcon label="" color="currentColor" />
							<VisuallyHidden>
								<FormattedMessage id="card.video.close" defaultMessage="Close video" />
							</VisuallyHidden>
						</button>
					</div>
				</ModalDialog>
			)
		: null;

	const card = (
		<Container
			css={[
				cardContainer,
				align === 'center' && centeredCardContainer,
				isDark ? cardContainerDark : cardContainerLight,
				orientation === 'vertical' && verticalOrientation,
				orientation === 'horizontal' && horizontalOrientation,
				isLinkableCard && linkableCardContainer,
				isLinkableCard && !isDark && linkableCardHoverShadow,
			]}
		>
			{spotIllustration ? (
				<div
					className="spotIllustrationContainer"
					css={[
						illustrationContainer,
						orientation === 'horizontal' && horizontalIllustrationContainer,
					]}
				>
					<Image
						src={spotIllustration.src}
						alt={spotIllustration.alt ?? ''}
						style={{
							height:
								spotIllustration.height ??
								(orientation === 'vertical' ? SPOT_ILLUSTRATION_MAX_HEIGHT : 'auto'),
							maxHeight: orientation === 'vertical' ? SPOT_ILLUSTRATION_MAX_HEIGHT : 'auto',
							width:
								orientation === 'horizontal' && !spotIllustration.height
									? SPOT_ILLUSTRATION_WIDTH_ON_HORIZONTAL_CARD
									: 'auto',
							maxWidth: orientation === 'horizontal' && spotIllustration.height ? 'auto' : '100%',
						}}
					/>
				</div>
			) : (
				image && (
					<div
						className={`cardImageContainer${videoSrc ? ' videoCardImageContainer' : ''}`}
						css={[
							cardImageContainer,
							orientation === 'vertical' && verticalImageContainer,
							orientation === 'vertical' && imageAspectRatio === '1 / 1' && squareImageAspectRatio,
							orientation === 'vertical' &&
								imageAspectRatio !== '1 / 1' &&
								widescreenImageAspectRatio,
							orientation === 'horizontal' && horizontalImageContainer,
							videoSrc && videoThumbnailContainer,
						]}
					>
						<Image
							css={[
								imageStyle,
								orientation === 'vertical' && verticalImageStyle,
								orientation === 'horizontal' && horizontalImageStyle,
								videoSrc && videoThumbnailImageStyle,
							]}
							{...image}
							src={image.src}
							alt={image.alt}
						/>
						{videoSrc && <PlayIcon />}
					</div>
				)
			)}
			<div
				css={[
					cardBodyContainer,
					orientation === 'vertical' && verticalBodyContainer,
					orientation === 'horizontal' && horizontalBodyContainer,
				]}
			>
				{logo && (
					<div css={resetMargin}>
						<Picture
							image={{
								src: cardLogoLockups[logo],
								alt: '',
								height: 24,
							}}
						/>
					</div>
				)}
				{iconUrl && (
					<div css={iconContainer}>
						<Image
							src={iconUrl}
							alt={''}
							style={{
								width: `${iconSizesPaddingIncluded[iconSize]}px`,
								height: `${iconSizesPaddingIncluded[iconSize]}px`,
								aspectRatio: '1',
								padding: `${
									paddingIncluded
										? 0
										: (iconSizesPaddingIncluded[iconSize] - iconSizesPaddingExcluded[iconSize]) / 2
								}px`,
								objectPosition: 'center',
							}}
						/>
					</div>
				)}
				{iconComponent && <div css={iconContainer}>{iconComponent}</div>}
				{eyebrow && (
					<p className="eyebrow-md" css={resetMargin}>
						{eyebrow}
					</p>
				)}
				{title && (
					<TitleComponent className="card-title" id={titleId} css={resetMargin}>
						{title}
					</TitleComponent>
				)}
				{body && bodyElement}
				{isLinkableCard && (
					<div css={[orientation === 'vertical' && verticalLinkContainer, linkContainer]}>
						<Link
							id={callToActionId}
							ariaLabelledby={`${callToActionId} ${titleId}`}
							type={videoSrc ? 'video' : 'primary'}
							{...callToAction}
							href={videoSrc ?? callToAction.href}
							anchorProps={videoSrc ? { onClick: openVideoOverlay } : callToAction.anchorProps}
							className={`full-card ${callToAction?.className ?? ''}`}
							bgColor={isDark ? 'bgBlackBlue' : 'bgWhite'}
							aria-label={callToAction.text}
						/>
					</div>
				)}
			</div>
		</Container>
	);

	const fullWidthGridStyles = css({
		marginInline: 'auto',
		maxWidth: '100%',
		width: '100%',
		'--width-without-gaps': 'calc(100% - var(--cl-gap) * 11)', // width of only the 12 columns, subtracting the 11 gaps in 12 column layout
		paddingInlineStart: `${WCL_GRID_PADDING_SM}px`,
		paddingInlineEnd: `${WCL_GRID_PADDING_SM}px`,
		[media.only.sm]: {
			paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
			paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`,
		},
		[media.above.sm]: {
			gridTemplateColumns: 'repeat(12, 1fr)',
		},
		[media.above.md]: {
			'--cl-gap': '32px',
			paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
			paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`,
			maxWidth: `${WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2}px`,
		},
		display: 'grid',
		boxSizing: 'border-box',
		gap: 'var(--cl-gap)',
		'--cl-gap': '24px',
		'--content-columns': `${numColumns === 10 ? 2 : 1} / span ${numColumns === 10 ? 10 : 12}`,
		[media.below.sm]: {
			'--content-columns': '1 / span 12',
		},
	});

	if (numColumns && orientation === 'horizontal') {
		return (
			<>
				{videoOverlay}
				<div
					css={css({
						width: '100%',
						height: '100%',
						display: 'flex',
						justifyContent: 'center',
					})}
				>
					<div css={[fullWidthGridStyles]}>{card}</div>
				</div>
			</>
		);
	}

	return (
		<>
			{videoOverlay}
			{card}
		</>
	);
};

const cardContainer = css({
	borderRadius: wacToken('corner.radius.card'),
	display: 'flex',
	gridColumn: 'var(--content-columns)',
});

const cardContainerLight = css({
	background: wacToken('color.neutral.light0'),
	border: `1px solid ${wacToken('color.neutral.light400')}`,
});

const cardContainerDark = css({
	background: wacToken('color.neutral.dark200'),
	border: `1px solid ${wacToken('color.neutral.dark100')}`,
	color: wacToken('color.neutral.light0'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'p, h2, h3, h4, h5, h6': {
		color: `${wacToken('color.neutral.light0')}`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div .eyebrow-md, div .card-title': {
		color: `${wacToken('color.neutral.light0')}`,
	},
});

const illustrationContainer = css({
	marginBlockStart: token('space.400'),
	marginInlineStart: token('space.400'),
	marginInlineEnd: token('space.400'),
	marginBlockEnd: token('space.100'),
});

const horizontalIllustrationContainer = css({
	marginInlineEnd: token('space.100'),
	[media.above.sm]: {
		marginBlockEnd: token('space.400'),
	},
});

const verticalOrientation = css({
	flexDirection: 'column',
});

const horizontalOrientation = css({
	flexDirection: 'row',
	height: '100%',
	width: '100%',
	// spec says 375px is mobile but our lowest media query is min-width 30rem;
	[media.below.xs]: {
		flexDirection: 'column',
	},
});

const centeredCardContainer = css({
	alignItems: 'center',
	textAlign: 'center',
});

const linkableCardContainer = css({
	position: 'relative',
	// Need to style anchors differently for a linkable card and cannot pass in CompiledCSS
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['.full-card']: {
		'::before': {
			content: '',
			inset: '0',
			position: 'absolute',
			zIndex: 1,
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['&:has(.full-card:hover) .videoCardImageContainer img, &:has(.full-card:focus-visible) .videoCardImageContainer img']:
		{
			transform: 'scale(1.06)',
		},
});

const linkableCardHoverShadow = css({
	transition: 'box-shadow 125ms ease-in',
	['&:has(.full-card:hover), &:has(.full-card:focus)']: {
		transition: 'box-shadow 125ms ease-out',
		boxShadow: token('elevation.shadow.overlay'),
	},
});

const cardImageContainer = css({
	overflow: 'clip',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

const horizontalImageContainer = css({
	borderStartStartRadius: wacToken('corner.radius.card'),
	borderEndStartRadius: wacToken('corner.radius.card'),
	height: '100%',
	width: '100%',
	display: 'flex',
});

const verticalImageContainer = css({
	borderStartStartRadius: wacToken('corner.radius.card'),
	borderStartEndRadius: wacToken('corner.radius.card'),
	flexShrink: 0,
	position: 'relative',
	width: '100%',
});

const widescreenImageAspectRatio = css({
	aspectRatio: '16 / 9',
});

const squareImageAspectRatio = css({
	aspectRatio: '1 / 1',
});

const imageStyle = css({
	height: '100%',
	width: '100%',
	display: 'flex',
});

const verticalImageStyle = css({
	inset: 0,
	objectFit: 'cover',
	position: 'absolute',
});

const horizontalImageStyle = css({
	margin: 'auto',
	objectFit: 'contain',
});

const videoThumbnailContainer = css({
	position: 'relative',
	'&::after': {
		backgroundColor: wacToken('color.neutral.dark200'),
		content: '',
		inset: 0,
		opacity: 0.15,
		pointerEvents: 'none',
		position: 'absolute',
	},
});

const videoThumbnailImageStyle = css({
	height: '100%',
	objectFit: 'cover',
	transform: 'scale(1)',
	transformOrigin: 'center center',
	transition: 'all 1.3s',
});

const verticalBodyContainer = css({
	flex: '1',
});

const horizontalBodyContainer = css({
	width: '100%',
	height: '100%',
	paddingBlockStart: token('space.400'),
});

const cardBodyContainer = css({
	padding: token('space.300'),
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
});

const resetMargin = css({
	margin: '0 !important',
});

const iconContainer = css({
	marginBlockEnd: token('space.200'),
});

const linkContainer = css({
	paddingBlockStart: token('space.200'),
});

const verticalLinkContainer = css({
	marginBlockStart: 'auto',
});

const videoOverlayContent = css({
	position: 'relative',
});

const videoOverlayCloseButton = css({
	alignItems: 'center',
	background: wacToken('color.neutral.dark200'),
	border: 0,
	borderRadius: '50%',
	color: wacToken('color.neutral.light0'),
	cursor: 'pointer',
	display: 'flex',
	insetBlockStart: token('space.100'),
	insetInlineEnd: token('space.100'),
	padding: token('space.075'),
	position: 'absolute',
	zIndex: 1,
	'&:focus-visible': {
		outline: `2px solid ${wacToken('color.neutral.light0')}`,
		outlineOffset: '2px',
	},
});
